import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const QuizLwa = () => {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/exercises/quiz-lwa").then(({ data }) => setQuestions(data.questions)).catch(() => {});
    api.get("/exercises/quiz-lwa/result").then(({ data }) => data?.result && setResult(data.result)).catch(() => {});
  }, []);

  const submit = async () => {
    const ordered = questions.map((_, i) => answers[i]).filter(Boolean);
    if (ordered.length < questions.length) {
      toast.error("Répondez à toutes les questions");
      return;
    }
    const { data } = await api.post("/exercises/quiz-lwa", { answers: ordered });
    setResult(data);
  };

  if (result) {
    return (
      <div className="border border-primary/30 rounded-lg p-8 bg-primary/5 text-center" data-testid="quiz-result">
        <p className="text-xs uppercase tracking-[0.3em] text-primary">{result.title}</p>
        <h3 className="font-serif-display text-4xl mt-3 text-foreground">{result.name}</h3>
        <p className="text-sm text-foreground/75 mt-5 leading-relaxed max-w-lg mx-auto">{result.description}</p>
        <button onClick={() => { setResult(null); setAnswers({}); }} className="mt-6 text-sm text-primary underline underline-offset-4" data-testid="quiz-retake-btn">
          {t("exercise.retake")}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="quiz-lwa">
      {questions.map((q, qi) => (
        <div key={qi}>
          <p className="font-medium text-foreground mb-3">{qi + 1}. {q.q}</p>
          <div className="space-y-2">
            {q.options.map((opt) => (
              <button key={opt} onClick={() => setAnswers((a) => ({ ...a, [qi]: opt }))}
                className={`block w-full text-left text-sm px-4 py-2.5 rounded-lg border transition-colors duration-200 ${answers[qi] === opt ? "border-primary bg-primary/10 text-foreground" : "border-primary/15 text-foreground/70 hover:border-primary/40"}`}
                data-testid={`quiz-option-${qi}-${q.options.indexOf(opt)}`}>
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button onClick={submit} className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-[#16528A] transition-colors duration-200" data-testid="quiz-submit-btn">
        {t("exercise.submit")}
      </button>
    </div>
  );
};

export const ExerciseBlock = ({ content, courseId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [, type, exerciseId, prompt] = content.split(":", 4);
  const [text, setText] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!user || type !== "reflection") return;
    api.get("/exercises/reflections").then(({ data }) => {
      const found = data.find((r) => r.exercise_id === exerciseId);
      if (found) { setText(found.content); setSaved(true); }
    }).catch(() => {});
  }, [user, exerciseId, type]);

  if (type === "quiz_lwa") {
    return (
      <div>
        <p className="text-foreground/80 leading-relaxed mb-8">{prompt}</p>
        {user ? <QuizLwa /> : <p className="text-sm text-muted-foreground">{t("common.loginRequired")}</p>}
      </div>
    );
  }

  const save = async () => {
    await api.post("/exercises/reflection", { course_id: courseId, exercise_id: exerciseId, content: text });
    setSaved(true);
    toast.success(t("exercise.saved"));
  };

  return (
    <div data-testid={`exercise-${exerciseId}`}>
      <p className="text-foreground/80 leading-relaxed italic border-l-2 border-primary/40 pl-4">{prompt}</p>
      {user ? (
        <>
          <textarea value={text} onChange={(e) => { setText(e.target.value); setSaved(false); }}
            rows={8} placeholder={t("exercise.placeholder")}
            className="w-full mt-6 bg-input border border-primary/20 rounded-lg p-4 text-sm leading-relaxed focus:ring-2 focus:ring-primary focus:outline-none"
            data-testid="reflection-textarea" />
          <button onClick={save} disabled={!text.trim()}
            className="mt-4 px-6 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-[#16528A] transition-colors duration-200 disabled:opacity-40"
            data-testid="reflection-save-btn">
            {saved ? t("exercise.saved") : t("exercise.save")}
          </button>
        </>
      ) : (
        <p className="text-sm text-muted-foreground mt-6">{t("common.loginRequired")}</p>
      )}
    </div>
  );
};
